import { motion } from 'framer-motion';
import { AlertTriangle, X, Check, ArrowRight } from 'lucide-react';
import AnimatedBackground from '../../components/AnimatedBackground';

const problems = [
  'Students never find out exactly why marks were deducted',
  'Generic feedback like "needs improvement" provides no actionable insight',
  'Teachers lack time to give detailed, personalized diagnostics',
  'Parents see grades but don\'t understand learning gaps',
  'Same mistakes repeat because root causes remain hidden',
  'Exam strategies go unanalyzed despite major mark losses'
];

export default function ProblemSection() {
  return (
    <section 
      data-testid="problem-section"
      className="relative py-20 md:py-32 overflow-hidden"
    >
      {/* Subtle Background Animation */}
      <div className="absolute inset-0 opacity-20">
        <AnimatedBackground variant="subtle" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6">
            <AlertTriangle className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium text-secondary">The Problem</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Marks are lost for reasons
            <br />
            <span className="text-secondary">students never see.</span>
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Traditional exam feedback tells you what you scored, but never explains why marks were lost. That's the gap Edalyze fills.
          </p>
        </motion.div>

        {/* Problem List */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-20"
        >
          {problems.map((problem, index) => (
             <motion.div
             key={index}
             initial={{ opacity: 0, x: -20 }}
             whileInView={{ opacity: 1, x: 0 }}
             viewport={{ once: true }}
             transition={{ delay: index * 0.1 }}
             className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-secondary/30 transition-colors"
           >
             <div className="p-1 rounded-full bg-secondary/10 shrink-0">
               <X className="w-4 h-4 text-secondary" />
             </div>
             <p className="text-sm text-muted-foreground leading-relaxed">{problem}</p>
           </motion.div>
          ))}
        </motion.div>

        {/* Before/After Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Before */}
            <div className="relative">
              <div className="absolute -inset-4 bg-secondary/5 rounded-3xl blur-xl" />
              <div className="relative bg-card rounded-2xl border border-border p-6 lg:p-8 h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-secondary/10">
                    <X className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold">Before Edalyze</h3>
                </div>
                
                <div className="space-y-4">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="p-4 rounded-xl bg-secondary/5 border border-secondary/10"
                  >
                    <p className="font-medium text-secondary mb-1">Misleading Confidence</p>
                    <p className="text-sm text-muted-foreground font-mono">"I studied Chapter 3 for 5 hours, so I must know it."</p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="p-4 rounded-xl bg-secondary/5 border border-secondary/10"
                  >
                    <p className="font-medium text-secondary mb-1">Unknown errors</p>
                    <p className="text-sm text-muted-foreground font-mono">22 marks lost — reason unclear</p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                    className="p-4 rounded-xl bg-secondary/5 border border-secondary/10"
                  >
                    <p className="font-medium text-secondary mb-1">No patterns</p>
                    <p className="text-sm text-muted-foreground font-mono">Mistakes feel random</p>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, type: "spring" }}
                className="p-4 rounded-full bg-primary shadow-xl shadow-primary/25"
              >
                <ArrowRight className="w-6 h-6 text-primary-foreground" />
              </motion.div>
            </div>

            {/* After */}
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/5 rounded-3xl blur-xl" />
              <div className="relative bg-card rounded-2xl border border-primary/20 p-6 lg:p-8 h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">After Edalyze</h3>
                </div>
                
                <div className="space-y-4">
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="p-4 rounded-xl bg-primary/5 border border-primary/10"
                  >
                    <p className="font-medium text-primary mb-1">Precise diagnosis</p>
                    <p className="text-sm text-muted-foreground font-mono">"8 marks lost: Concept Correct, but Derivation Steps Missing."</p>
                  </motion.div>
                  
                  {/* --- NEW SECTION: CROSS-SKILL DIAGNOSIS --- */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="p-4 rounded-xl bg-primary/5 border border-primary/10"
                  >
                    <p className="font-medium text-primary mb-1">Root Cause Analysis</p>
                    <div className="text-sm font-mono space-y-1">
                      <p className="text-muted-foreground">Physics Concept: <span className="text-green-500">Strong (90%)</span></p>
                      <p className="text-muted-foreground">Math Blocker: <span className="text-red-500 font-bold">Weak (Quadratic Equations)</span></p>
                    </div>
                  </motion.div>
                  {/* ------------------------------------------ */}

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                    className="p-4 rounded-xl bg-primary/5 border border-primary/10"
                  >
                    <p className="font-medium text-primary mb-1">Clear patterns</p>
                    <p className="text-sm text-muted-foreground font-mono"> Recurring weakness in integration problems</p>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
